import { clone, cloneDeep, isEmpty } from 'lodash-es'
import useMasterDataLookup from '../../composables/masterDataLookup'
import type { IDataExistConstraint, IField, IImportModel } from '@/modules/admin/type'
import type { ITxDataTableColumn, ITxDataTableVisibleColumn } from '@/shared/components/txDataTable/TxDataTable.types'
import { AttributeType } from '@/models/catalogAttribute'
import { useUserStore } from '@/store/userData'
import utils from '@/services/utils'
import { appConstants, privileges } from '@/models/constants'

export default class ArticleModel {
  modelPath: string
  fields: IField[]
  importInfo: Record<string, any>

  constructor(t) {
    this.modelPath = 'catalogs.articles'
    this.importInfo = {
      validateStateEditableFields: true,
      validateRequiredLookupTableAttributes: true,
      isCatalogSpecificModel: true,
      uniqueConstraints: [
        { name: 'Constraint 1', fields: ['ArticleNumber'] },
      ],
    }
    // NOTE: Always use the clone of fields never change the property directly on field
    this.fields = this.getFields(t)
  }

  private getFields(t) {
    const userStore = useUserStore()
    const enablePricesSegmentationsInArticlesList = userStore.activeCatalog && userStore.activeCatalog!.Config.EnablePricesSegmentationsInArticlesList
    const { getModelsLookup, getSizeScalesLookup, getMasterSizeScalesLookup, getRetailWindowsLookup, getArticleStatesLookup, getColorsLookup, getArticlesLookup, getLinkedCatalogArticleNumberAttributesLookup, getSellerModelsLookup } = useMasterDataLookup()
    const statusAttributeTypeFilterLookup = new Map([
      [-1, t('general.all')],
      [1, t('general.active')],
      [0, t('general.inactive')],
    ])
    let articleStatusFilterLookUp = new Map([
      [-1, t('general.all')],
      [1, t('general.active')],
      [0, t('general.inactive')],
    ])
    if (userStore.activeCatalog?.DataSourceTypeId === appConstants.catalogTypes.inherited) {
      articleStatusFilterLookUp = new Map([
        [-1, t('general.all')],
        [1, t('general.active')],
        [3, t('general.globallyDropped')],
      ])
      // for Inherited Catalog dont show not assorted articles articles if the UI_ViewUnAssortedArticles is not assigned.
      if (userStore.userProfile.isValidPrivilege(privileges.article.viewUnAssortedArticles)) {
        articleStatusFilterLookUp.set(2, t('general.notAssorted'))
      }
    }

    const fields: IField[] = [
      { SystemName: 'Id', DisplayName: t('admin.articles.fields.articleId'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Int, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ArticleNumber', DisplayName: t('admin.articles.fields.articleNumber'), Visible: true, Creatable: true, Editable: true, AttributeType: AttributeType.Nvarchar, IsPersonal: false, IsStatic: true, IsRequired: true, maxLength: 20, minLength: 1, pattern: '^[^ ]*(?: [^ ]+)* ?$', autoMap: ['article', 'articlenumber', 'article number', 'colorway number', 'colorwaynumber', 'artnbr', 'style color code'], messages: { pattern: t('validations.consecutiveWhiteSpaceNotAllowed') }, column: -1 } as IField,
      { SystemName: 'ModelId', DisplayName: t('admin.articles.fields.modelId'), Visible: false, Creatable: true, Editable: false, AttributeType: AttributeType.Lookup, lookupSource: getModelsLookup, lookupIdField: 'ModelId', lookupDisplayField: 'ModelNumber', IsRequired: false, maxLength: -1, minLength: 1 } as IField,
      { SystemName: 'ModelId', DisplayName: t('admin.articles.fields.modelId'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Int, IsRequired: false, maxLength: -1, minLength: 1 } as IField,
      { SystemName: 'ModelNumber', DisplayName: t('admin.articles.fields.modelNumber'), Visible: true, Creatable: true, Editable: false, showInEdit: true, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: 200, minLength: 0, autoMap: ['model', 'modelnumber', 'model number', 'style number', 'stylenumber', 'stylenbr'] } as IField,
      { SystemName: 'ArticleName', DisplayName: t('admin.articles.fields.modelName'), Visible: false, Creatable: true, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: true, maxLength: 100, minLength: 1 } as IField,
      { SystemName: 'ModelName', DisplayName: t('admin.articles.fields.modelName'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: true, maxLength: 100, minLength: 1 } as IField,
      // { SystemName: 'MasterModelId', DisplayName: t('admin.articles.fields.modelId'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Int, IsRequired: false, maxLength: -1, minLength: 1} as IField,
      { SystemName: 'LicenseeModelNumber', DisplayName: t('admin.articles.fields.licenseeModelNumber'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: 200, minLength: 0 } as IField,
      { SystemName: 'ParentModelNumber', DisplayName: t('admin.articles.fields.parentModelNumber'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: 200, minLength: 0 } as IField,
      { SystemName: 'ParentModelName', DisplayName: t('admin.articles.fields.parentModelName'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: 200, minLength: 0 } as IField,
      { SystemName: 'ColorCode', DisplayName: t('admin.articles.fields.colorCode'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Int, IsPersonal: false, IsStatic: true, prefixZeros: 3, IsRequired: false, maxLength: -1, minLength: 1, pattern: '^(0*[1-9][0-9]{0,2}|100|999)$', messages: { pattern: t('validations.positiveColorCode') } } as IField,
      { SystemName: 'ColorName', DisplayName: t('admin.articles.fields.colorName'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'HexCode', DisplayName: t('admin.articles.fields.hexCode'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: 7, minLength: 0 } as IField,
      { SystemName: 'ColorGroup', DisplayName: t('admin.articles.fields.colorGroup'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: 250, minLength: 0 } as IField,
      { SystemName: 'ColorType', DisplayName: t('admin.articles.fields.colorType'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: 250, minLength: 0 } as IField,
      { SystemName: 'ColorAssetId', DisplayName: t('admin.articles.fields.colorAssetId'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: 250, minLength: 0 } as IField,
      { SystemName: 'RAID', DisplayName: t('admin.articles.fields.RAID'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Int, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'RAN', DisplayName: t('admin.articles.fields.RAN'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'PRAID', DisplayName: t('admin.articles.fields.PRAID'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Int, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'PRAN', DisplayName: t('admin.articles.fields.PRAN'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'Status', DisplayName: t('admin.articles.fields.status'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Status, parentField: 'PS', IsRequired: false, maxLength: -1, minLength: 0, FilterLookup: articleStatusFilterLookUp } as IField,
      { SystemName: 'DropReason', DisplayName: t('admin.articles.fields.dropReason'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'PS', DisplayName: t('admin.articles.fields.parentStatus'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Status, IsRequired: false, maxLength: -1, minLength: 0, FilterLookup: statusAttributeTypeFilterLookup } as IField,
      { SystemName: 'Locked', DisplayName: t('admin.articles.fields.locked'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.BoolInt, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ArticleSource', DisplayName: t('admin.articles.fields.articleSource'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'PSizeScale', DisplayName: t('admin.articles.fields.pSizeScale'), Visible: false, Creatable: false, Editable: false, showInEdit: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'PSizeScaleId', DisplayName: t('admin.articles.fields.pSizeScaleId'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'SizeScaleId', DisplayName: t('admin.articles.fields.sizeScaleId'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Int, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'SizeScaleId', DisplayName: t('admin.articles.fields.sizeScaleId'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Lookup, lookupSource: getSizeScalesLookup, lookupIdField: 'SizeScaleId', lookupDisplayField: 'SizeScale', IsRequired: true, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'MasterSizeScale', DisplayName: t('admin.articles.fields.masterSizeScale'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'SizeScale', DisplayName: t('admin.articles.fields.sizeScale'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'MasterSizeScaleId', DisplayName: t('admin.articles.fields.masterSizeScaleId'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Int, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'MasterSizeScaleId', DisplayName: t('admin.articles.fields.masterSizeScaleId'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Lookup, lookupSource: getMasterSizeScalesLookup, lookupIdField: 'MasterSizeScaleId', lookupDisplayField: 'SizeScale', IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'SortOrder', DisplayName: t('admin.articles.fields.sortOrder'), Visible: true, Creatable: true, Editable: true, AttributeType: AttributeType.Int, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'CreatedDate', DisplayName: t('admin.articles.fields.createdDate'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Date, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'UpdatedDate', DisplayName: t('admin.articles.fields.updatedDate'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Date, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
    ]
    if (userStore.myAttributes) {
      const myAttributes = Object.values(userStore.myAttributes!).filter(o => !o.IsRequest && !o.IsStatic && !o.IsPersonal)
      myAttributes.forEach((attribute) => {
        const newAtt: IField = clone(attribute) as IField
        fields.push(newAtt)
      })
    }
    const staticFields = [
      { SystemName: 'Created_By_User', DisplayName: t('admin.articles.fields.createdBy'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'Updated_By_User', DisplayName: t('admin.articles.fields.updatedBy'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      // TODO: when we this field need to check, what should be correct attribute type
      { SystemName: 'isArticleLockingDisable', Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.BoolInt, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'BlockImage', DisplayName: t('admin.articles.fields.blockImage'), Visible: true, Creatable: true, Editable: true, AttributeType: AttributeType.BoolInt, IsRequired: false, maxLength: -1, minLength: 0 } as IField,

      // retail window fields
      { SystemName: 'PShipmentStartDate', DisplayName: t('admin.articles.fields.pShipmentStartDate'), Visible: false, Creatable: false, Editable: false, showInEdit: false, AttributeType: AttributeType.DateTime, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'PShipmentEndDate', DisplayName: t('admin.articles.fields.pShipmentEndDate'), Visible: false, Creatable: false, Editable: false, showInEdit: false, AttributeType: AttributeType.DateTime, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'PRetailIntroMonth', DisplayName: t('admin.articles.fields.pRetailIntroMonth'), Visible: false, Creatable: false, Editable: false, showInEdit: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'PRetailExitMonth', DisplayName: t('admin.articles.fields.pRetailExitMonth'), Visible: false, Creatable: false, Editable: false, showInEdit: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'PPeriod', DisplayName: t('admin.articles.fields.pPeriod'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'PShipmentWindowRangeId', DisplayName: t('admin.articles.fields.pShipmentWindowRangeId'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'RetailIntroMonth', DisplayName: t('admin.articles.fields.retailIntroMonth'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'RetailExitMonth', DisplayName: t('admin.articles.fields.retailExitMonth'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'Period', DisplayName: t('admin.articles.fields.period'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'RetailIntroMonthId', DisplayName: t('admin.articles.fields.retailIntroMonth'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Lookup, lookupSource: getRetailWindowsLookup, lookupIdField: 'ShipmentWindowRangeId', lookupDisplayField: 'RetailIntroMonth', useLookupName: true, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'RetailExitMonthId', DisplayName: t('admin.articles.fields.retailExitMonth'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Lookup, lookupSource: getRetailWindowsLookup, lookupIdField: 'ShipmentWindowRangeId', lookupDisplayField: 'RetailExitMonth', useLookupName: true, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ShipmentStartDate', DisplayName: t('admin.articles.fields.shipmentStartDate'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.DateTime, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ShipmentEndDate', DisplayName: t('admin.articles.fields.shipmentEndDate'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.DateTime, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'IsOutOfShippingRange', DisplayName: t('admin.articles.fields.isOutOfShippingRange'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.BoolInt, IsRequired: false, maxLength: -1, minLength: 0 } as IField,

      // these two fields used internally
      { SystemName: 'StateRank', DisplayName: t('admin.articles.fields.stateRank'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Int, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ModelStateId', DisplayName: t('admin.articles.fields.modelStateId'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ModelStateName', DisplayName: t('admin.articles.fields.modelStateName'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ModelStateRank', DisplayName: t('admin.articles.fields.modelStateRank'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Int, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'AllowRetailWindowUpdateOnChild', DisplayName: t('admin.articles.fields.allowRetailWindowUpdateOnChild'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.BoolInt, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'AllowRetailWindowUpdate', DisplayName: t('admin.articles.fields.allowRetailWindowUpdate'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.BoolInt, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'SeasonalSequence', DisplayName: t('admin.articles.fields.seasonalSequence'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Int, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ArticleLifecycle', DisplayName: t('admin.articles.fields.articleLifecycle'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ModelLifecycle', DisplayName: t('admin.articles.fields.modelLifecycle'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ModelSourceSeason', DisplayName: t('admin.articles.fields.modelSourceSeason'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 1 } as IField,
      { SystemName: 'ArticleSourceSeason', DisplayName: t('admin.articles.fields.articleSourceSeason'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 1 } as IField,
      { SystemName: 'ActivationDate', DisplayName: t('admin.articles.fields.activationDate'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Date, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'DropDate', DisplayName: t('admin.articles.fields.dropDate'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Date, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ModelCreatedDate', DisplayName: t('admin.articles.fields.modelCreatedDate'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Date, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ModelDropDate', DisplayName: t('admin.articles.fields.modelDropDate'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Date, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ModelActivationDate', DisplayName: t('admin.articles.fields.modelActivationDate'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Date, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'SourceRequestId', DisplayName: t('admin.articles.fields.sourceRequestId'), Visible: true, Creatable: false, Editable: true, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'SourceRequestNumber', DisplayName: t('admin.articles.fields.sourceRequestNumber'), Visible: true, Creatable: false, Editable: true, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ColoredUpBy', DisplayName: t('admin.articles.fields.coloredUpBy'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'ColoredUpDate', DisplayName: t('admin.articles.fields.coloredUpDate'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Date, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      // isfilterCriteria is used to filter colors based on the criteria, adding color at the end to validate the color criteria.
      { SystemName: 'ColorId', DisplayName: t('admin.articles.fields.primaryColor'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Lookup, lookupSource: getColorsLookup, lookupIdField: 'ColorId', lookupDisplayField: 'ColorName', isfilterCriteria: true, IsRequired: false, maxLength: -1, minLength: 0 } as IField,

      // state level fields
      { SystemName: 'StateId', DisplayName: t('admin.articles.fields.stateId'), Visible: false, Creatable: false, Editable: false, AttributeType: AttributeType.Lookup, lookupSource: getArticleStatesLookup, lookupIdField: 'StateId', lookupDisplayField: 'StateName', IsRequired: true, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'StateId', DisplayName: t('admin.articles.fields.stateId'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0 } as IField,
      { SystemName: 'StateName', DisplayName: t('admin.articles.fields.stateName'), Visible: true, Creatable: false, Editable: false, AttributeType: AttributeType.Nvarchar, IsRequired: false, maxLength: -1, minLength: 0, autoMap: ['Article State', 'Colorway State'] } as IField,
    ]
    fields.push(...staticFields)
    if (userStore.activeCatalog) {
      userStore.articleStateList.forEach((state) => {
        if (state.Status) {
          const stateField: IField = {
            SystemName: `StatesTimestamp.${state.StateId}`,
            DisplayName: `State TimeStamp: ${state.StateName}`,
            AttributeType: AttributeType.Date,
            Visible: true,
            Creatable: false,
            Editable: false,
            IsRequired: false,
            IsStatic: true,
            IsPersonal: false,
          }
          fields.push(stateField)
        }
      })
      if (enablePricesSegmentationsInArticlesList) {
        userStore.activeCatalog.CatalogPriceGroupList.forEach((priceGroup) => {
          if (priceGroup.Status) {
            const priceField: IField = {
              SystemName: `_Prices.${priceGroup.Id}`,
              DisplayName: `P:${priceGroup.Name} (${priceGroup.CurrencyCode})`,
              AttributeType: AttributeType.Decimal,
              Visible: true,
              Creatable: false,
              Editable: false,
              IsRequired: false,
              IsStatic: true,
              IsPersonal: false,
            }
            fields.push(priceField)
          }
        })
        Object.values(userStore.activeCatalog._IndexedCatalogSegmentation).forEach((seg) => {
          if (seg.Status && seg.Viewable) {
            const segField: IField = {
              SystemName: `_Segmentations.${seg.Id}`,
              DisplayName: `S:${seg.Name}`,
              AttributeType: AttributeType.Nvarchar,
              Visible: true,
              Creatable: false,
              Editable: false,
              IsRequired: false,
              IsStatic: true,
              IsPersonal: false,
            }
            fields.push(segField)
          }
        })
      }
    }
    // transform model
    // if auto numbering enabled article number and model number fields will be not creatable and ColorId will be enabled for create and edit
    if (userStore.sellerDetails?.AutomaticNumbering === 1) {
      const articleNumberField = fields.filter(field => field.SystemName === 'ArticleNumber')[0]
      if (utils.isDefined(articleNumberField)) {
        articleNumberField.Creatable = false
        articleNumberField.Editable = false
      }
      const modelNumberField = fields.filter(field => field.SystemName === 'ModelNumber')[0]
      if (utils.isDefined(modelNumberField)) {
        modelNumberField.Creatable = false
        modelNumberField.Editable = false
      }
      const colorIdField = fields.filter(field => field.SystemName === 'ColorId')[0]
      if (utils.isDefined(colorIdField)) {
        colorIdField.Creatable = true
        colorIdField.Editable = true
        colorIdField.showInEdit = true
      }
      const colorCodeField = fields.filter(field => field.SystemName === 'ColorCode')[0]
      if (utils.isDefined(colorCodeField)) {
        colorCodeField.Visible = true
      }
      const hexCodeField = fields.filter(field => field.SystemName === 'HexCode')[0]
      if (utils.isDefined(hexCodeField)) {
        hexCodeField.Visible = true
      }
      const colorNameField = fields.filter(field => field.SystemName === 'ColorName')[0]
      if (utils.isDefined(colorNameField)) {
        colorNameField.Visible = true
      }
      const colorGroupField = fields.filter(field => field.SystemName === 'ColorGroup')[0]
      if (utils.isDefined(colorGroupField)) {
        colorGroupField.Visible = true
      }
      const colorTypeField = fields.filter(field => field.SystemName === 'ColorType')[0]
      if (utils.isDefined(colorTypeField)) {
        colorTypeField.Visible = true
      }
      const colorAssetIdField = fields.filter(field => field.SystemName === 'ColorAssetId')[0]
      if (utils.isDefined(colorAssetIdField)) {
        colorAssetIdField.Visible = true
      }
    }

    if (userStore.activeCatalog?.IsShippingWindowApply === 1) {
      const allowRetailWindowUpdateField = fields.filter(field => field.SystemName === 'AllowRetailWindowUpdate')[0]
      if (utils.isDefined(allowRetailWindowUpdateField)) {
        allowRetailWindowUpdateField.Visible = true
      }

      const allowRetailWindowUpdateOnChild = fields.filter(field => field.SystemName === 'AllowRetailWindowUpdateOnChild')[0]
      if (utils.isDefined(allowRetailWindowUpdateOnChild)) {
        allowRetailWindowUpdateOnChild.Visible = true
        allowRetailWindowUpdateOnChild.Creatable = true
        allowRetailWindowUpdateOnChild.Editable = true
      }
    }

    // role based restriction: if retail window is set as not viweable do not show retail window related fields
    if (utils.isDefined(userStore.activeCatalog?._RoleRestrictedRetailWindow) && !isEmpty(userStore.activeCatalog?._RoleRestrictedRetailWindow) && userStore.activeCatalog?._RoleRestrictedRetailWindow.NotViewable) {
      const retailIntroMonthField = fields.filter(field => field.SystemName === 'RetailIntroMonth')[0]
      if (utils.isDefined(retailIntroMonthField)) {
        retailIntroMonthField.Visible = false
      }
      const retailExitMonthField = fields.filter(field => field.SystemName === 'RetailExitMonth')[0]
      if (utils.isDefined(retailExitMonthField)) {
        retailExitMonthField.Visible = false
      }
      const periodField = fields.filter(field => field.SystemName === 'Period')[0]
      if (utils.isDefined(periodField)) {
        periodField.Visible = false
      }
      const shipmentStartDateField = fields.filter(field => field.SystemName === 'ShipmentStartDate')[0]
      if (utils.isDefined(shipmentStartDateField)) {
        shipmentStartDateField.Visible = false
      }
      const shipmentEndDateField = fields.filter(field => field.SystemName === 'ShipmentEndDate')[0]
      if (utils.isDefined(shipmentEndDateField)) {
        shipmentEndDateField.Visible = false
      }
    }

    if (userStore.activeCatalog!.IsPriceByStyleApply) {
      if (userStore.sellerDetails?.AutomaticNumbering !== 1) {
        const modelNumberField = fields.filter(field => field.SystemName === 'ModelNumber')[0]
        if (utils.isDefined(modelNumberField)) {
          modelNumberField.IsRequired = true
        }
      }
      else {
        // if auto numbering enabled modelId lookup field will be mandatory
        const modelIdField = fields.filter(field => field.SystemName === 'ModelId' && field.AttributeType === AttributeType.Lookup)[0]
        if (utils.isDefined(modelIdField)) {
          modelIdField.IsRequired = true
        }
        // if auto numbering enabled modelId number field will be visible
        const modelIdNumberField = fields.filter(field => field.SystemName === 'ModelId' && field.AttributeType === AttributeType.Int)[0]
        if (utils.isDefined(modelIdNumberField)) {
          modelIdNumberField.Visible = true
        }
      }
    }

    // make setSizes itemAction and listAction visible if IsSizeScaleByStyle is enabled at seller
    if (userStore.sellerDetails?.IsSizeScaleByStyle) {
      // make SizeScale field visible
      const SizeScaleField = fields.filter(field => field.SystemName === 'SizeScale')[0]
      if (utils.isDefined(SizeScaleField)) {
        SizeScaleField.Visible = true
      }
      const masterSizeScaleIdField = fields.filter(field => field.SystemName === 'MasterSizeScaleId' && field.AttributeType === AttributeType.Int)[0]
      if (utils.isDefined(masterSizeScaleIdField)) {
        masterSizeScaleIdField.Visible = true
      }
      const masterSizeScaleField = fields.filter(field => field.SystemName === 'MasterSizeScale')[0]
      if (utils.isDefined(masterSizeScaleField)) {
        masterSizeScaleField.Visible = true
      }
      const modelIdField = fields.filter(field => field.SystemName === 'SizeScaleId')[0]
      if (utils.isDefined(modelIdField)) {
        modelIdField.Visible = true
      }
    }

    if (userStore.activeCatalog?.DataSourceTypeId === 3) {
    // disable block image edit/create for child catalog as this flag is now cascaded from master catalog
      const blockImageField = fields.filter(field => field.SystemName === 'BlockImage')[0]
      if (utils.isDefined(blockImageField)) {
        blockImageField.Creatable = false
        blockImageField.Editable = false
      }
      // make Overridable attributes editable and creatable for inherited catalogs if its required
      const overridableAttributes = fields.filter(field => field.Overridable)
      if (utils.isDefined(overridableAttributes) && overridableAttributes.length) {
        overridableAttributes.forEach((attribute) => {
          if (attribute.IsRequired) {
            attribute.Editable = true
          }
          if (attribute.IsRequired) {
            attribute.Creatable = true
          }
        })
      }
      // make parent_model_type attribute creatable/editable false
      const parentModelTypeField = fields.filter(field => field.SystemName === 'parent_model_type')[0]
      if (utils.isDefined(parentModelTypeField)) {
        parentModelTypeField.Creatable = false
        parentModelTypeField.Editable = false
      }

      // modify child catalog Articles Fields
      // add Is Assigned field used internally
      const IsAccessibleField: IField = Object.assign({}, appConstants.staticFieldTemplate, {
        AttributeType: AttributeType.IntBool,
        DisplayName: t('admin.articles.fields.isAssigned'),
        SystemName: 'IsAccessible',
        Creatable: false,
        Editable: false,
        Visible: false,
        column: -1,
      })
      fields.push(IsAccessibleField)

      const PRANfield = fields.filter(field => field.SystemName === 'PRAN')[0]
      if (utils.isDefined(PRANfield)) {
        PRANfield.Visible = true
      }
      const PSfield = fields.filter(field => field.SystemName === 'PS')[0]
      if (utils.isDefined(PSfield)) {
        PSfield.Visible = true
      }

      if (userStore.activeCatalog?.IsShippingWindowApply === 1) {
        // make parent shipping range visible and read only properties on edit dialog for inherited articles
        const pRetailIntroMonthField = fields.filter(field => field.SystemName === 'PRetailIntroMonth')[0]
        if (utils.isDefined(pRetailIntroMonthField)) {
          pRetailIntroMonthField.Visible = true
          pRetailIntroMonthField.showInEdit = true
        }
        const pRetailExitMonthField = fields.filter(field => field.SystemName === 'PRetailExitMonth')[0]
        if (utils.isDefined(pRetailExitMonthField)) {
          pRetailExitMonthField.Visible = true
          pRetailExitMonthField.showInEdit = true
        }

        // make parent shipping start date visible and read only properties on edit dialog for inherited articles
        const pShipmentStartDateField = fields.filter(field => field.SystemName === 'PShipmentStartDate')[0]
        if (utils.isDefined(pShipmentStartDateField)) {
          pShipmentStartDateField.Visible = true
          pShipmentStartDateField.showInEdit = true
        }

        // make parent shipping end date visible and read only properties on edit dialog for inherited articles
        const pShipmentEndDateField = fields.filter(field => field.SystemName === 'PShipmentEndDate')[0]
        if (utils.isDefined(pShipmentEndDateField)) {
          pShipmentEndDateField.Visible = true
          pShipmentEndDateField.showInEdit = true
        }

        const pPeriodField = fields.filter(field => field.SystemName === 'PPeriod')[0]
        if (utils.isDefined(pPeriodField)) {
          pPeriodField.Visible = true
          pPeriodField.showInEdit = true
        }
      }
      if (userStore.sellerDetails?.IsSizeScaleByStyle) {
        // make parent size scale visible and read only properties on edit dialog for inherited articles
        const pSizeScaleField = fields.filter(field => field.SystemName === 'PSizeScale')[0]
        if (utils.isDefined(pSizeScaleField)) {
          pSizeScaleField.Visible = true
        }

        // make parent size scale visible for inherited articles
        const pSizeScaleIdField = fields.filter(field => field.SystemName === 'PSizeScaleId')[0]
        if (utils.isDefined(pSizeScaleIdField)) {
          pSizeScaleIdField.Visible = true
        }
      }
    }

    // add lookup surce for articlenumber attributes
    const articleNumberAttributes = fields.filter(field => field.AttributeType === AttributeType.ArticleNumber)
    if (utils.isDefined(articleNumberAttributes) && articleNumberAttributes.length) {
      articleNumberAttributes.forEach((attribute) => {
        attribute.lookupSource = getArticlesLookup
        attribute.lookupIdField = 'Id'
        attribute.lookupDisplayField = 'ArticleNumber'
      })
    }

    // add lookup source for linkedcatalogarticlenumber attributes
    const linkedCatalogArticleNumberAttributes = fields.filter(field => field.AttributeType === AttributeType.LinkedCatalogArticleNumber)
    if (utils.isDefined(linkedCatalogArticleNumberAttributes) && linkedCatalogArticleNumberAttributes.length) {
      linkedCatalogArticleNumberAttributes.forEach((attribute) => {
        attribute.lookupSource = getLinkedCatalogArticleNumberAttributesLookup
        attribute.lookupIdField = 'Id'
        attribute.lookupDisplayField = 'ArticleNumber'
      })
    }

    // add lookup source for modelnumber attributes
    const modelNumberAttributes = fields.filter(field => field.AttributeType === AttributeType.ModelNumber)
    if (utils.isDefined(modelNumberAttributes) && modelNumberAttributes.length) {
      modelNumberAttributes.forEach((attribute) => {
        attribute.lookupSource = getSellerModelsLookup
        attribute.lookupIdField = 'ModelId'
        attribute.lookupDisplayField = 'ModelNumber'
      })
    }

    // non calc attributes with validation expression
    const attributesWithValidationExpression = fields.filter(field => field.AttributeType !== AttributeType.Calc && utils.isDefined(field.ValidationExpression) && field.ValidationExpression.length)
    if (utils.isDefined(attributesWithValidationExpression) && attributesWithValidationExpression.length) {
      attributesWithValidationExpression.forEach((attribute) => {
        if (utils.tryParse(attribute.ValidationExpression!)) {
          attribute.txPattern = attribute.ValidationExpression !== '{}' ? attribute.ValidationExpression : ''
          attribute.pattern = ''
        }
        else {
          attribute.txPattern = ''
          attribute.pattern = attribute.ValidationExpression != null && attribute.ValidationExpression !== '' && attribute.ValidationExpression !== '{}' ? attribute.ValidationExpression : ''
        }
        const message = (attribute.ValidationMessage && attribute.ValidationMessage !== '') ? attribute.ValidationMessage : t('validations.pattern')

        if (!attribute.messages) {
          attribute.messages = {}
        }

        attribute.messages.pattern = message
      })
    }

    // make self attribute creatable or editable if they are required attributes or creatable and editable respectively
    // and if source is parent disable create and edit
    const attributeSourceParentAttributes = fields.filter(field => utils.isDefined(field.AttributeSource) && field.AttributeSource?.toUpperCase() !== 'SELF')
    if (utils.isDefined(attributeSourceParentAttributes) && attributeSourceParentAttributes.length) {
      attributeSourceParentAttributes.forEach((attribute) => {
        attribute.Editable = false
        attribute.Creatable = false
      })
    }
    const attributeSourceSelfAttributes = fields.filter(field => utils.isDefined(field.AttributeSource) && field.AttributeSource?.toUpperCase() === 'SELF' && field.AttributeType !== AttributeType.Calc)
    if (utils.isDefined(attributeSourceSelfAttributes) && attributeSourceSelfAttributes.length) {
      attributeSourceSelfAttributes.forEach((attribute) => {
        if (attribute.IsRequired) {
          attribute.Editable = true
        }
        if (attribute.IsRequired) {
          attribute.Creatable = true
        }
      })
    }
    return fields
  }

  getDataTableColumns() {
    const dataTableFields: ITxDataTableColumn[] = this.fields.filter(field => field.Visible && !field.IsPersonal)
      .map(o => ({
        property: o.SystemName,
        title: o.DisplayName,
        filterLookup: o.FilterLookup,
        type: o.AttributeType,
        lookupDisplayField: o.lookupDisplayField,
        lookupSource: o.lookupSource,
        lookupIdField: o.lookupIdField,
        useLookupName: o.useLookupName,
      }))
    return dataTableFields
  }

  getDefaultVisibleColumnList() {
    // Default column list
    const defaultColumns = ['ArticleNumber', 'ModelNumber', 'ArticleName', 'Status', 'UpdatedDate']

    const defaultFieldObjects: ITxDataTableVisibleColumn[] = this.fields.filter(field =>
      defaultColumns.includes(field.SystemName) && field.Visible && !field.IsPersonal,
    ).map(o => ({
      property: o.SystemName,
      order: 1,
      width: 0.175,
    }))

    // Filter dynamic attributes that should be visible by default with VisibleInListByDefault as true
    const dynamicAttributes: ITxDataTableVisibleColumn[] = this.fields
      .filter(field => !field.IsStatic && !field.IsPersonal && field.Visible && field.VisibleInListByDefault)
      .slice(0, 5)
      .map(o => ({
        property: o.SystemName,
        order: 1,
        width: 0.175,
      }))

    const updatedDateIndex = defaultFieldObjects.findIndex(field => field.property === 'UpdatedDate')

    // Combine default fields with dynamic attributes before UpdatedDate
    const combinedFields: ITxDataTableVisibleColumn[] = [
      ...defaultFieldObjects.slice(0, updatedDateIndex),
      ...dynamicAttributes,
      ...defaultFieldObjects.slice(updatedDateIndex),
    ]

    return combinedFields
  }

  getImportData(action: string) {
    const userStore = useUserStore()
    const { getColorsLookup, getDropReasons, getSizeScalesLookup, getMasterSizeScalesLookup, getArticleStatesLookup, getSellerDatasetValuesLookup } = useMasterDataLookup()

    let importFields: IField[] = []
    const importInfo = cloneDeep(this.importInfo)
    if (action === 'import') {
      importInfo.uniqueConstraints = [{ name: 'Constraint 1', fields: ['ArticleNumber', 'ModelNumber'] }]

      for (let i = 0; i < this.fields.length; i++) {
        const field = clone(this.fields[i])

        // make LinkedCatalogArticleNumber and lookuptable type attributes not creatable through import
        if (field.AttributeType === AttributeType.LinkedCatalogArticleNumber || field.AttributeType === AttributeType.LookupTable || field.AttributeType === AttributeType.Calc) {
          continue
        }

        if (field.SystemName === 'ArticleNumber') {
          if (userStore.sellerDetails?.AutomaticNumbering === 1) {
            field.Creatable = true
            if (userStore.activeCatalog!.DataSourceTypeId === 3) {
              field.IsRequired = true
            }
            else {
              field.IsRequired = false
              field.minLength = 0
            }
          }
          importFields.push(field)
        }
        if (field.SystemName === 'ModelNumber') {
          if (userStore.sellerDetails?.AutomaticNumbering === 1) {
            field.Creatable = true
            field.minLength = 0
          }
          importFields.push(field)
        }
        else if (field.SystemName === 'Status') {
          field.Creatable = true
          importFields.push(field)
        }
        else if (field.SystemName === 'DropReason') {
          if (userStore.activeCatalog!.DataSourceTypeId !== 3) {
            // enable Drop Reason column for mapping as an optional field
            field.Creatable = true
            // make the DropReason column mandatory while validation and not mandatory while mapping
            field.isRequiredWhileValidating = true
            const dataExistsConstraints: IDataExistConstraint = { lookupSource: getDropReasons }
            dataExistsConstraints.model = { lookupDisplayField: 'Reason' }
            field.dataExistConstraints = dataExistsConstraints
            importFields.push(field)
          }
        }
        else if (field.SystemName === 'ColorId') {
          // primary color is only updatable for master catalog
          if (userStore.activeCatalog!.DataSourceTypeId !== 3) {
            field.Creatable = true
            importFields.push(field)
          }
        }
        else if (field.SystemName === 'StateName') {
          field.Creatable = true
          const dataExistsConstraints: IDataExistConstraint = { lookupSource: getArticleStatesLookup }
          dataExistsConstraints.model = { lookupDisplayField: 'StateName' }
          field.dataExistConstraints = dataExistsConstraints
          importFields.push(field)
        }
        else if (field.SystemName === 'SizeScale') {
          field.Creatable = true
          const dataExistsConstraints: IDataExistConstraint = { lookupSource: getSizeScalesLookup }
          dataExistsConstraints.model = { lookupDisplayField: 'SizeScale' }
          field.dataExistConstraints = dataExistsConstraints
          importFields.push(field)
        }
        else if (field.SystemName === 'MasterSizeScale') {
          field.Creatable = true
          const dataExistsConstraints: IDataExistConstraint = { lookupSource: getMasterSizeScalesLookup }
          dataExistsConstraints.model = { lookupDisplayField: 'SizeScale' }
          field.dataExistConstraints = dataExistsConstraints
          importFields.push(field)
        }
        else if ((field.SystemName === 'RetailIntroMonthId' && field.AttributeType === AttributeType.Lookup)
          || (field.SystemName === 'RetailExitMonthId' && field.AttributeType === AttributeType.Lookup)) {
          // make Shipment Range Field creatable when "DummyArticleApplyRetailWindow" privilege is assigned
          if (userStore.userProfile.isValidPrivilege('UpdateArticleShipmentWindowRange') && userStore.activeCatalog?.IsShippingWindowApply === 1) {
            if ((utils.isDefined(userStore.activeCatalog?._RoleRestrictedRetailWindow) && !isEmpty(userStore.activeCatalog?._RoleRestrictedRetailWindow) && !userStore.activeCatalog?._RoleRestrictedRetailWindow.NotEditable) || (isEmpty(userStore.activeCatalog?._RoleRestrictedRetailWindow))) {
              field.Creatable = true
              importFields.push(field)
            }
          }
        }
        else if (field.Creatable) {
          if (field.SystemName === 'ModelId' || field.SystemName === 'ArticleNumber' || field.SystemName === 'ModelNumber') {
            continue
          }
          if (field.SystemName === 'parent_model_type') {
            // make ParentModelNumber creatable
            const parentModelNumberField = clone(this.fields.find(field => field.SystemName === 'ParentModelNumber'))
            if (parentModelNumberField) {
              parentModelNumberField.Creatable = true
              importFields.push(parentModelNumberField)
            }
          }
          else if (field.AttributeType === AttributeType.ColorPalette) { // add lookup source for colorpalatte attributes
            field.lookupSource = getColorsLookup
            field.isfilterCriteria = true
            field.lookupIdField = 'ColorName'
            field.lookupDisplayField = 'ColorId'
          }
          else if (field.AttributeType === AttributeType.Dataset) { // add lookup source for dataset attributes
            field.lookupSource = getSellerDatasetValuesLookup
            field.lookupIdField = 'Id'
            field.lookupDisplayField = 'DisplayValue'
          }
          else if (field.SystemName === 'ArticleName') {
            // if configured model name length then change the allowed max length to the configured value
            const configuredModelNameLength = userStore.sellerDetails?.ModelNameLength || 100
            if (configuredModelNameLength !== 100) {
              field.maxLength = configuredModelNameLength
            }
          }
          importFields.push(field)
        }
      }
    }
    else if (action === 'updateViaImport') {
      importInfo.uniqueConstraints = [{ name: 'Constraint 1', fields: ['ArticleNumber'] }]

      for (let i = 0; i < this.fields.length; i++) {
        const field = clone(this.fields[i])

        // make LinkedCatalogArticleNumber and lookuptable type attributes not creatable through import
        if (field.AttributeType === AttributeType.LinkedCatalogArticleNumber || field.AttributeType === AttributeType.LookupTable || field.AttributeType === AttributeType.Calc) {
          continue
        }

        if (field.SystemName === 'ArticleNumber') {
          field.IsRequired = true
          importFields.push(field)
        }
        else if (field.SystemName === 'ColorId') {
          // primary color is only updatable for master catalog
          if (userStore.activeCatalog!.DataSourceTypeId !== 3) {
            importFields.push(field)
          }
        }
        else if ((field.SystemName === 'RetailIntroMonthId' && field.AttributeType === AttributeType.Lookup)
          || (field.SystemName === 'RetailExitMonthId' && field.AttributeType === AttributeType.Lookup)) {
          // make Shipment Range Field creatable when "DummyArticleApplyRetailWindow" privilege is assigned
          if (userStore.userProfile.isValidPrivilege('UpdateArticleShipmentWindowRange') && userStore.activeCatalog?.IsShippingWindowApply === 1) {
            if ((utils.isDefined(userStore.activeCatalog?._RoleRestrictedRetailWindow) && !isEmpty(userStore.activeCatalog?._RoleRestrictedRetailWindow) && !userStore.activeCatalog?._RoleRestrictedRetailWindow.NotEditable) || (isEmpty(userStore.activeCatalog?._RoleRestrictedRetailWindow))) {
              importFields.push(field)
            }
          }
        }
        else if (field.Creatable) {
          if (field.SystemName === 'ModelId' || field.SystemName === 'ArticleNumber' || field.SystemName === 'ModelNumber' || field.SystemName === 'ArticleName') {
            continue
          }
          if (field.SystemName === 'parent_model_type') {
            // make ParentModelNumber creatable
            const parentModelNumberField = clone(this.fields.find(field => field.SystemName === 'ParentModelNumber'))
            if (parentModelNumberField) {
              importFields.push(parentModelNumberField)
            }
          }
          else if (field.AttributeType === AttributeType.ColorPalette) { // add lookup source for colorpalatte attributes
            field.lookupSource = getColorsLookup
            field.isfilterCriteria = true
            field.lookupIdField = 'ColorName'
            field.lookupDisplayField = 'ColorId'
          }
          else if (field.AttributeType === AttributeType.Dataset) { // add lookup source for dataset attributes
            field.lookupSource = getSellerDatasetValuesLookup
            field.lookupIdField = 'Id'
            field.lookupDisplayField = 'DisplayValue'
          }
          if (!field.IsStatic) {
            field.isRequiredIfMapped = field.IsRequired
            field.IsRequired = false
          }
          importFields.push(field)
        }
      }
    }
    else if (action === 'updateViaImportModel') {
      // make model number as unique constraint
      importInfo.uniqueConstraints = [{ name: 'Constraint 1', fields: ['ModelNumber'] }]

      for (let i = 0; i < this.fields.length; i++) {
        const field = clone(this.fields[i])

        if (field.AttributeType === AttributeType.LinkedCatalogArticleNumber || field.AttributeType === AttributeType.LookupTable || field.AttributeType === AttributeType.Calc) {
          continue
        }

        if (field.SystemName === 'ModelNumber') {
          field.IsRequired = true
          importFields.push(field)
        }
        else if (field.Editable) {
          if (field.SystemName === 'parent_model_type') {
            // make ParentModelNumber creatable
            const parentModelNumberField = clone(this.fields.find(field => field.SystemName === 'ParentModelNumber'))
            if (parentModelNumberField) {
              importFields.push(parentModelNumberField)
            }
          }
          else if (field.AttributeType === AttributeType.ColorPalette) {
            field.lookupSource = getColorsLookup
            field.isfilterCriteria = true
            field.lookupIdField = 'ColorName'
            field.lookupDisplayField = 'ColorId'
            importFields.push(field)
          }
          else if (field.AttributeType === AttributeType.Dataset) { // add lookup source for dataset attributes
            field.lookupSource = getSellerDatasetValuesLookup
            field.lookupIdField = 'Id'
            field.lookupDisplayField = 'DisplayValue'
          }
          else if (field.IsModelLevel) {
            field.isRequiredIfMapped = field.IsRequired
            field.IsRequired = false
            importFields.push(field)
          }
        }
      }
    }
    else if (action === 'importByLicensee') {
      // if Article Attrbute or Model Attribute is assigned at catalog level then make those attributes mandatory
      const articleAttribute = userStore.activeCatalog?.ArticleAttribute
      const modelAttribute = userStore.activeCatalog?.ModelAttribute

      for (let i = 0; i < this.fields.length; i++) {
        const field = clone(this.fields[i])

        // make LinkedCatalogArticleNumber and lookuptable type attributes not creatable through import
        if (field.AttributeType === AttributeType.LinkedCatalogArticleNumber || field.AttributeType === AttributeType.LookupTable || field.AttributeType === AttributeType.Calc) {
          continue
        }

        if (field.SystemName === 'Status') {
          importFields.push(field)
        }
        else if (field.SystemName === 'DropReason') {
          if (userStore.activeCatalog!.DataSourceTypeId !== 3) {
            // enable Drop Reason column for mapping as an optional field
            // make the DropReason column mandatory while validation and not mandatory while mapping
            field.isRequiredWhileValidating = true
            const dataExistsConstraints: IDataExistConstraint = { lookupSource: getDropReasons }
            dataExistsConstraints.model = { lookupDisplayField: 'Reason' }
            field.dataExistConstraints = dataExistsConstraints
            importFields.push(field)
          }
        }
        else if (field.SystemName === 'ColorId') {
          // primary color is only updatable for master catalog
          if (userStore.activeCatalog!.DataSourceTypeId !== 3) {
            importFields.push(field)
          }
        }
        else if (field.SystemName === 'StateName') {
          field.Creatable = true
          const dataExistsConstraints: IDataExistConstraint = { lookupSource: getArticleStatesLookup }
          dataExistsConstraints.model = { lookupDisplayField: 'StateName' }
          field.dataExistConstraints = dataExistsConstraints
          importFields.push(field)
        }
        else if (field.SystemName === 'SizeScale') {
          field.Creatable = true
          const dataExistsConstraints: IDataExistConstraint = { lookupSource: getSizeScalesLookup }
          dataExistsConstraints.model = { lookupDisplayField: 'SizeScale' }
          field.dataExistConstraints = dataExistsConstraints
          importFields.push(field)
        }
        else if (field.SystemName === 'MasterSizeScale') {
          field.Creatable = true
          const dataExistsConstraints: IDataExistConstraint = { lookupSource: getMasterSizeScalesLookup }
          dataExistsConstraints.model = { lookupDisplayField: 'SizeScale' }
          field.dataExistConstraints = dataExistsConstraints
          importFields.push(field)
        }
        else if ((field.SystemName === 'RetailIntroMonthId' && field.AttributeType === AttributeType.Lookup)
          || (field.SystemName === 'RetailExitMonthId' && field.AttributeType === AttributeType.Lookup)) {
          // make Shipment Range Field creatable when "DummyArticleApplyRetailWindow" privilege is assigned
          if (userStore.userProfile.isValidPrivilege('UpdateArticleShipmentWindowRange') && userStore.activeCatalog?.IsShippingWindowApply === 1) {
            if ((utils.isDefined(userStore.activeCatalog?._RoleRestrictedRetailWindow) && !isEmpty(userStore.activeCatalog?._RoleRestrictedRetailWindow) && !userStore.activeCatalog?._RoleRestrictedRetailWindow.NotEditable) || (isEmpty(userStore.activeCatalog?._RoleRestrictedRetailWindow))) {
              importFields.push(field)
            }
          }
        }
        else if ((utils.isDefined(articleAttribute) && articleAttribute.toString().trim() !== '' && field.SystemName.toUpperCase() === articleAttribute.toUpperCase())
          || (utils.isDefined(modelAttribute) && modelAttribute.toString().trim() !== '' && field.SystemName.toUpperCase() === modelAttribute.toUpperCase())) {
          // add unique constraints
          importInfo.uniqueConstraints = [{ name: 'Constraint 1', fields: [articleAttribute] }]
          field.minLength = 1
          field.IsRequired = true
          importFields.push(field)
        }

        else if (field.Creatable) {
          if (field.SystemName === 'ModelId' || field.SystemName === 'ArticleNumber' || field.SystemName === 'ModelNumber') {
            continue
          }
          if (field.SystemName === 'parent_model_type') {
            // make ParentModelNumber creatable
            const parentModelNumberField = clone(this.fields.find(field => field.SystemName === 'ParentModelNumber'))
            if (parentModelNumberField) {
              importFields.push(parentModelNumberField)
            }
          }
          else if (field.AttributeType === AttributeType.ColorPalette) { // add lookup source for colorpalatte attributes
            field.lookupSource = getColorsLookup
            field.isfilterCriteria = true
            field.lookupIdField = 'ColorName'
            field.lookupDisplayField = 'ColorId'
          }
          else if (field.AttributeType === AttributeType.Dataset) { // add lookup source for dataset attributes
            field.lookupSource = getSellerDatasetValuesLookup
            field.lookupIdField = 'Id'
            field.lookupDisplayField = 'DisplayValue'
          }
          else if (field.SystemName === 'ArticleName') {
            // if configured model name length then change the allowed max length to the configured value
            const configuredModelNameLength = userStore.sellerDetails?.ModelNameLength || 100
            if (configuredModelNameLength !== 100) {
              field.maxLength = configuredModelNameLength
            }
          }
          importFields.push(field)
        }
      }
    }
    else if (action === 'setSizesByLicenseeModelNumber') {
      importFields = cloneDeep(this.fields)
      const sizesModelFields: Array<IField> = []
      const sizeScaleField = importFields.filter(field => field.SystemName === 'SizeScale')[0]
      if (utils.isDefined(sizeScaleField)) {
        sizeScaleField.IsRequired = true
        const dataExistsConstraints: IDataExistConstraint = { lookupSource: getSizeScalesLookup }
        dataExistsConstraints.model = { lookupDisplayField: 'SizeScale' }
        sizeScaleField.dataExistConstraints = dataExistsConstraints
      }
      const masterSizeScaleField = importFields.filter(field => field.SystemName === 'MasterSizeScale')[0]
      if (utils.isDefined(masterSizeScaleField)) {
        masterSizeScaleField.IsRequired = true
        const dataExistsConstraints: IDataExistConstraint = { lookupSource: getMasterSizeScalesLookup }
        dataExistsConstraints.model = { lookupDisplayField: 'SizeScale' }
        masterSizeScaleField.dataExistConstraints = dataExistsConstraints
      }
      const licenseeModelNumberField = importFields.filter(field => field.SystemName === 'LicenseeModelNumber')[0]
      if (utils.isDefined(licenseeModelNumberField)) {
        licenseeModelNumberField.IsRequired = true
      }
      sizesModelFields.push(licenseeModelNumberField)
      sizesModelFields.push(masterSizeScaleField)
      sizesModelFields.push(sizeScaleField)

      // add unique constraints
      importInfo.uniqueConstraints = [{ name: 'Constraint 1', fields: ['LicenseeModelNumber'] }]

      importFields = sizesModelFields
    }
    else if (action === 'setPricesByLicenseeModelNumber') {
      importFields = cloneDeep(this.fields)
      const priceByLicenseeModelFields: Array<IField> = []
      const licenseeModelNumberField = importFields.filter(field => field.SystemName === 'LicenseeModelNumber')[0]
      if (utils.isDefined(licenseeModelNumberField)) {
        licenseeModelNumberField.IsRequired = true
      }
      priceByLicenseeModelFields.push(licenseeModelNumberField)
      const priceGroupIds: number[] = []
      userStore.activeCatalog!.CatalogPriceGroupList.forEach((priceGroup) => {
        if (priceGroup.Status) {
          priceGroupIds.push(priceGroup.Id)
          const priceField: IField = Object.assign({}, appConstants.staticFieldTemplate, {
            AttributeType: AttributeType.Decimal,
            DisplayName: `P:${priceGroup.Name} (${priceGroup.CurrencyCode})`,
            SystemName: priceGroup.Id,
            Creatable: true,
            column: -1,
            pattern: `^[+]?\\d+([.]\\d{0,${userStore.activeCatalog!.Config.NumberOfDecimalsForPrices}})?$`,
            messages: { pattern: `Invalid price value. It should be a positive number and not contain more than ${userStore.activeCatalog!.Config.NumberOfDecimalsForPrices} decimals.` },
          })
          priceByLicenseeModelFields.push(priceField)
        }
      })
      // add unique constraint
      importInfo.uniqueConstraints = [{ name: 'Constraint 1', fields: ['LicenseeModelNumber'] }]
      importInfo.atLeastOneRequiredConstraints = [
        { name: 'Constraint 1', fields: priceGroupIds },
      ]
      importInfo.validateArticleLocking = true

      importFields = priceByLicenseeModelFields
    }
    return { importInfo, modelPath: this.modelPath, fields: importFields } as IImportModel
  }
}
